import { Dialog, DialogActions, DialogContent } from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import { DoraButton, DoraSelect, DoraTextInput } from "dora-shared/frontend";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../../../../redux-store";
import { loadSubcontractors } from "../../../../ducks/data/subcontractors";
import { useSelector } from "react-redux";
import { selectSubcontractors } from "../../../../ducks/data/subcontractors/selectors";
import { shareCargoWithSubcontractor } from "../../../../ducks/app/cargo-dialog";

const ShareCargoDialog = ({
  onClose,
  cargoId,
}: {
  onClose: () => void;
  cargoId: string;
}) => {
  const { t } = useTranslation(["components", "shipmentModal"]);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const dispatch = useAppDispatch();
  const subcontractors = useSelector(selectSubcontractors);
  const [selectedSubcontractor, setSelectedSubcontractor] = useState<
    string | null
  >(null);
  const [contractorRef, setContractorRef] = useState("");

  const options = useMemo(
    () =>
      subcontractors.map((s) => ({
        label: s.name,
        value: s.id,
      })),
    [subcontractors]
  );

  const shareButtonDisabled = useMemo(
    () => requestInProgress || !selectedSubcontractor,
    [requestInProgress, selectedSubcontractor]
  );

  useEffect(() => {
    dispatch(loadSubcontractors());
  }, [dispatch]);

  const onShareClick = async () => {
    if (!selectedSubcontractor) {
      return;
    }
    setRequestInProgress(true);
    dispatch(
      shareCargoWithSubcontractor({
        cargoId,
        contractId: selectedSubcontractor,
        contractorRef,
      })
    )
      .unwrap()
      .then(() => onClose())
      .catch(() => {
        // The shareCargoWithSubcontractor thunk has already reported it and shown the error toast.
      })
      .finally(() => setRequestInProgress(false));
  };

  return (
    <Dialog open>
      <DialogTitle>{t("shipmentModal:shareDialog.title")}</DialogTitle>
      <DialogContent>
        <div>{t("shipmentModal:shareDialog.text")}</div>
        <div className="mt-2">
          <DoraSelect
            className="w-full"
            fullWidth
            options={options}
            value={selectedSubcontractor ?? undefined}
            onChange={(v) => setSelectedSubcontractor(v)}
          />
        </div>
        <div className="mt-4">
          <div>Contractor ref.</div>
          <div className="mt-2">
            <DoraTextInput value={contractorRef} onChange={setContractorRef} />
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <div className="flex gap-2 items-center">
          <DoraButton
            variant="primary"
            onClick={onClose}
            disabled={requestInProgress}
          >
            {t("components:buttonLabels.cancel")}
          </DoraButton>
          <DoraButton
            variant="primary-filled"
            onClick={onShareClick}
            disabled={shareButtonDisabled}
          >
            {t("components:buttonLabels.continue")}
          </DoraButton>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default ShareCargoDialog;
